/**
 * SA host page — `/admin/bias-guardrails` (B-22b Surface 2).
 *
 * HoD spec §2: host-scoped page (NOT TenantDetail tab). Reuses
 * `<LockedGuardrailsPanel>` for current version + history table for prior
 * versions + two-step modal for "Issue new version".
 *
 * HoD acceptance gate #1: LockedGuardrailsPanel imported from
 * `@/components/common/bias/LockedGuardrailsPanel` (single-sourced; same
 * import path as Surface 1's BiasGuardrailsSection).
 */
import { useState, type JSX } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { PageHeader } from "@/components/admin/PageHeader";
import { Button } from "@/components/common/Button";
import { LockedGuardrailsPanel } from "@/components/common/bias/LockedGuardrailsPanel";
import { IssueNewVersionModal } from "@/components/admin/bias/IssueNewVersionModal";

interface VersionRow {
  version: number;
  guardrails_text: string;
  effective_at: string | null;
  issued_by_super_admin_user_id: string | null;
  issued_by_email: string | null;
  issue_reason: string;
  created_at: string | null;
}

interface VersionsResponse {
  versions: VersionRow[];
}

function useAdminBiasGuardrails() {
  return useQuery<VersionsResponse>({
    queryKey: ["admin-bias-guardrails"],
    queryFn: async () => {
      const r = await apiClient.get("/admin/bias-guardrails");
      return r.data as VersionsResponse;
    },
  });
}

export default function AdminBiasGuardrails(): JSX.Element {
  const queryClient = useQueryClient();
  const versionsQ = useAdminBiasGuardrails();
  const [modalOpen, setModalOpen] = useState(false);

  const issueMut = useMutation({
    mutationFn: async (payload: { guardrails_text: string; issue_reason: string }) => {
      const r = await apiClient.post("/admin/bias-guardrails", payload);
      return r.data;
    },
    onSuccess: () => {
      setModalOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["admin-bias-guardrails"] });
    },
  });

  if (versionsQ.isLoading) {
    return (
      <div className="text-sm text-text-secondary" aria-busy="true">
        Loading bias guardrails…
      </div>
    );
  }
  if (versionsQ.isError || !versionsQ.data) {
    return (
      <div className="text-sm text-status-rejected-fg">
        Couldn&apos;t load bias guardrails.
      </div>
    );
  }

  const versions = versionsQ.data.versions;
  const current = versions[0]; // desc order; index 0 = highest effective_at

  return (
    <div
      data-testid="admin-bias-guardrails-page"
      className="space-y-5 p-5 text-text-primary"
    >
      <PageHeader title="Bias guardrails" />
      <p className="text-sm text-text-secondary">
        Versioned, locked instruction block applied to every tenant AI call.
        Issuing a new version affects every tenant immediately.
      </p>

      {/* Current version */}
      {current ? (
        <section
          className="space-y-3 rounded-xl border border-border-default bg-surface-elevated p-4 dark:bg-surface-card"
          data-testid="current-version-panel"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-medium">Current version</h2>
            <Button
              variant="primary"
              onClick={() => setModalOpen(true)}
              data-testid="issue-new-version-button"
            >
              Issue new version
            </Button>
          </div>
          <LockedGuardrailsPanel
            text={current.guardrails_text}
            version={current.version}
            effectiveAt={current.effective_at}
          />
        </section>
      ) : null}

      {/* History table */}
      <section
        className="rounded-xl border border-border-default bg-surface-elevated p-4 dark:bg-surface-card"
        data-testid="version-history-section"
      >
        <h2 className="mb-3 font-medium">Version history</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-default text-left text-text-secondary">
              <th className="py-2 pr-4">Version</th>
              <th className="py-2 pr-4">Effective</th>
              <th className="py-2 pr-4">Issued by</th>
              <th className="py-2">Reason</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr
                key={v.version}
                className="border-b border-border-default last:border-0"
              >
                <td className="py-2 pr-4 font-mono">v{v.version}</td>
                <td className="py-2 pr-4 tabular-nums">
                  {v.effective_at
                    ? new Date(v.effective_at).toISOString().slice(0, 10)
                    : "—"}
                </td>
                <td className="py-2 pr-4 text-xs">{v.issued_by_email ?? "—"}</td>
                <td
                  className="py-2 text-xs"
                  title={v.issue_reason}
                >
                  {v.issue_reason.length > 80
                    ? `${v.issue_reason.slice(0, 80)}…`
                    : v.issue_reason}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {current ? (
        <IssueNewVersionModal
          isOpen={modalOpen}
          currentText={current.guardrails_text}
          currentVersion={current.version}
          onCancel={() => setModalOpen(false)}
          onIssue={async (text, reason) => {
            await issueMut.mutateAsync({
              guardrails_text: text,
              issue_reason: reason,
            });
          }}
        />
      ) : null}
    </div>
  );
}
